import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router';
import { DEFAULT_CHAT_TITLE } from '@/features/sessions/model/chat.constants';
import { logger } from '@/lib/utils/logger';
import type { SessionsGetChatInfoFn } from '../SessionsLayout.types';

const MAX_RETRIES = 5;
const RETRY_INTERVAL = 3000;

export function useChatTitle(getChatInfo: SessionsGetChatInfoFn) {
  const { chatId } = useParams<{ chatId: string }>();
  const [chatTitle, setChatTitle] = useState<string | undefined>(undefined);
  const retryCountRef = useRef(0);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const resolvedRef = useRef(false);

  useEffect(() => {
    setChatTitle(undefined);
    if (!chatId) return;

    resolvedRef.current = false;

    const fetchChatTitle = async () => {
      if (resolvedRef.current) return;
      try {
        const data = await getChatInfo({ contextId: chatId });
        if (data.title && data.title !== DEFAULT_CHAT_TITLE) {
          resolvedRef.current = true;
          setChatTitle(data.title);
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
        }
      } catch (error) {
        logger.error('Failed to fetch chat info:', error);
      }
    };

    void fetchChatTitle();

    retryCountRef.current = 0;
    intervalIdRef.current = setInterval(() => {
      retryCountRef.current += 1;

      if (retryCountRef.current >= MAX_RETRIES) {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        return;
      }

      void fetchChatTitle();
    }, RETRY_INTERVAL);

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [chatId, getChatInfo]);

  /**
   * A rename is authoritative, so it also ends the polling that would otherwise
   * write the server's older title back over it. Without this the two share one
   * setter and the poller wins whichever lands last, reverting the name the user
   * just typed with nothing on screen to explain it.
   */
  const commitTitle = useCallback((title: string) => {
    resolvedRef.current = true;
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    setChatTitle(title);
  }, []);

  return { chatTitle, setChatTitle, commitTitle };
}
